import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { AiPrices, AiPricesService } from '../../service/ai/ai-prices.service';
import {
  AI_COST_DEFAULTS, AI_COST_FIELDS, AI_COST_SCENARIOS, AiCostField, AiCostRow, AiCostScenario, AiCostValues, aiCostScenario,
  aiMoney, aiPlatformIcon, clampAiCost, priceAiCost, WORDS_PER_TOKEN,
} from '../../util/ai-cost';
import { CgOption } from '../cg-select/cg-select.component';

/** what the card reports after each change; `source` says which control moved */
export interface AiCostCardChange {
  values: AiCostValues;
  scenarioKey: string;
  modelId: string | null;
  source: 'value' | 'scenario' | 'model';
}

/**
 * The AI Cost Calculator card (owner 2026-10-19, after its OG cover): what you are building and the three
 * questions at the left (More Options folded under them, scrolling inside the column), the monthly bill on the
 * cheapest model or the one picked at the right. It reads GET /ai/prices itself; the numbers, the use and the
 * model come in and every change goes out (cardChange), so the page keeps its url and the home box its state.
 */
@Component( {
  selector: 'app-ai-cost-widget',
  templateUrl: './ai-cost-widget.component.html',
  styleUrls: [ './ai-cost-widget.component.scss' ],
} )
export class AiCostWidgetComponent implements OnInit, OnChanges, OnDestroy {

  @Input() values: AiCostValues = { ...AI_COST_DEFAULTS };
  @Input() scenarioKey = AI_COST_SCENARIOS[ 0 ].key;
  /** null = the cheapest model */
  @Input() modelId: string | null = null;
  @Output() cardChange = new EventEmitter<AiCostCardChange>();

  readonly scenarioOptions: CgOption[] = AI_COST_SCENARIOS.map( sc => ( { value: sc.key, label: sc.label } ) );
  readonly mainFields = AI_COST_FIELDS.filter( f => !f.more );
  readonly moreFields = AI_COST_FIELDS.filter( f => f.more );

  data: AiPrices | null = null;
  platformKeys: string[] = [];
  /** the model list, platform by platform, each model by name */
  modelOptions: CgOption[] = [];
  scenario: AiCostScenario = AI_COST_SCENARIOS[ 0 ];

  private rows: AiCostRow[] = [];
  private sub?: Subscription;

  constructor( private prices: AiPricesService ) {
  }

  ngOnInit(): void {
    this.sub = this.prices.prices$.subscribe( data => {
      this.data = data;
      this.platformKeys = Object.keys( data.platforms );
      const byName = [ ...data.models ].sort( ( a, b ) => a.name.localeCompare( b.name, 'en', { numeric: true } ) );
      this.modelOptions = this.platformKeys.reduce( ( list: CgOption[], key ) => list.concat(
        byName.filter( m => m.platform === key ).map( m => ( { value: m.id, label: `${ m.name } · ${ this.platformLabel( key ) }`, icon: aiPlatformIcon( key ) } ) ) ), [] );
      this.price();
    } );
  }

  ngOnChanges(): void {
    this.scenario = aiCostScenario( this.scenarioKey );
    this.price();
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  /** the card's row: the picked model, else the cheapest */
  get chosen(): AiCostRow | null {
    const picked = this.modelId && this.rows.find( r => r.model.id === this.modelId );
    return picked || this.cheapest;
  }

  get cheapest(): AiCostRow | null {
    return this.rows[ 0 ] || null;
  }

  /** "1,000 chat messages a day" */
  get usage(): string {
    const n = this.values.requests;
    return `${ this.count( n ) } ${ n === 1 ? this.scenario.one : this.scenario.many } a day`;
  }

  /** "Oct 19, 2026" */
  get syncedDay(): string {
    return AiPricesService.day( this.data?.syncedAt ?? null );
  }

  /** the field's value as the visitor sees it: words for the two length questions */
  shown( field: AiCostField ): number {
    const v = this.values[ field.key ];
    return field.words ? Math.round( v * WORDS_PER_TOKEN ) : v;
  }

  tokenNote( field: AiCostField ): string {
    return `About ${ this.count( this.values[ field.key ] ) } tokens`;
  }

  setValue( field: AiCostField, raw: string | number ): void {
    const n = Number( raw );
    if ( !isFinite( n ) ) { return; }
    this.values = { ...this.values, [ field.key ]: clampAiCost( field, field.words ? n / WORDS_PER_TOKEN : n ) };
    // a question of the use's own changes the use to Something Else; the finer settings keep it
    if ( !field.more ) { this.scenario = aiCostScenario( 'custom' ); }
    this.emit( 'value' );
  }

  /** One step up or down with the box's own arrows (the native ones can't take the site's colours). */
  nudge( field: AiCostField, direction: 1 | -1, event: Event ): void {
    event.preventDefault();
    this.setValue( field, this.shown( field ) + direction * ( field.step || 1 ) );
  }

  setScenarioKey( key: string ): void {
    this.scenario = aiCostScenario( key );
    if ( this.scenario.values ) { this.values = { ...this.values, ...this.scenario.values }; }
    this.emit( 'scenario' );
  }

  pickModelId( id: string ): void {
    this.modelId = id;
    this.emit( 'model' );
  }

  pickModel( row: AiCostRow ): void {
    this.pickModelId( row.model.id );
  }

  /** More Options opened: the questions' column scrolls down to them (the card keeps its height) */
  onMoreToggle( event: Event ): void {
    const more = event.target as HTMLDetailsElement;
    const column = more.parentElement;
    if ( more.open && column && column.scrollHeight > column.clientHeight ) {
      column.scrollTo( { top: column.scrollHeight, behavior: 'smooth' } );
    }
  }

  platformLabel( key: string ): string {
    return this.data?.platforms[ key ]?.label || key;
  }

  money( value: number ): string {
    return aiMoney( value );
  }

  count( value: number ): string {
    return Math.round( value ).toLocaleString( 'en-US' );
  }

  private price(): void {
    this.rows = this.data ? priceAiCost( this.data.models, this.values ) : [];
  }

  private emit( source: AiCostCardChange[ 'source' ] ): void {
    this.price();
    this.scenarioKey = this.scenario.key;
    this.cardChange.emit( { values: this.values, scenarioKey: this.scenario.key, modelId: this.modelId, source } );
  }
}
